/**
 * 패널 플래닝 — 대시보드 Module Hierarchy 책갈피 재사용 어댑터
 */
import { MODULE_HIERARCHY_ROW_KIND } from '@/features/dashboard-map/hierarchy/moduleHierarchyKeys';
import {
  createMapHierarchyBookmarkOverlayElement,
  resolveBookmarkAnchorPosition
} from '@/features/dashboard-map/hierarchy/mapHierarchyBookmarkLabel';
import { shouldShowMapSelectionOverlay } from '@/features/dashboard-map/hierarchy/mapFeatureClickPolicy';
import {
  readStringDisplayNoFromTreeItem,
  readStringSeqFromTreeItem
} from '@/features/panel-planning/utils/panelHierarchyMappers';
import { resolveHierarchyFromTreeItem, findArrayTreeItemBySeq } from '@/features/panel-planning/utils/mapHierarchySelectionContext';
import {
  getPanelMapFeaturesForArray,
  getPanelMapFeaturesForString
} from '@/features/panel-planning/utils/panelMapFeatureIndex';
import { getPanelTreeModuleChildren } from '@/features/panel-planning/utils/panelTreeModuleChildrenIndex.js';
import {
  readArrayDisplayNo,
  readArraySeq,
  readModuleDisplayNo,
  readModuleSeq,
  readStringDisplayNo,
  readStringSeq
} from '@/features/panel-planning/utils/panelMapFeatureProps';

function readArraySeqFromTreeItem(item) {
  if (!item) return null;
  if (item.seq != null && item.type === 'array') return Number(item.seq);
  if (item.solarArraySeq != null) return Number(item.solarArraySeq);
  const match = String(item.id || '').match(/^array_(\d+)$/);
  return match ? Number(match[1]) : null;
}

function readArrayDisplayNoFromTreeItem(item) {
  if (!item) return null;
  const raw = item.displayNo ?? item.arrayId ?? item.solarArrayId;
  return raw != null && raw !== '' ? String(raw) : null;
}

function readModuleSeqFromTreeItem(item) {
  if (!item) return null;
  if (item.seq != null && item.type === 'module') return Number(item.seq);
  if (item.solarModuleSeq != null) return Number(item.solarModuleSeq);
  const match = String(item.id || '').match(/^module_(\d+)$/);
  return match ? Number(match[1]) : null;
}

function readModuleDisplayNoFromTreeItem(item) {
  if (!item) return null;
  const raw = item.displayNo ?? item.moduleId ?? item.solarModuleId;
  return raw != null && raw !== '' ? String(raw) : null;
}

/** 트리 노드 → 대시보드 hierarchy target (책갈피 세그먼트용) */
export function panelTreeItemToHierarchyTarget(item, treeData) {
  if (!item?.type) return null;

  const { array, string } = resolveHierarchyFromTreeItem(treeData, item);

  if (item.type === 'array') {
    return {
      kind: MODULE_HIERARCHY_ROW_KIND.ARRAY,
      seq: readArraySeqFromTreeItem(item),
      displayId: readArrayDisplayNoFromTreeItem(item),
      arrayDisplayId: readArrayDisplayNoFromTreeItem(item)
    };
  }

  if (item.type === 'string') {
    const seq = readStringSeqFromTreeItem(item);
    return {
      kind: MODULE_HIERARCHY_ROW_KIND.STRING,
      seq: seq != null ? Number(seq) : null,
      displayId: readStringDisplayNoFromTreeItem(item),
      stringDisplayId: readStringDisplayNoFromTreeItem(item),
      arraySeq: readArraySeqFromTreeItem(item),
      arrayDisplayId: readArrayDisplayNoFromTreeItem(array)
    };
  }

  if (item.type === 'module') {
    const stringSeq = readStringSeqFromTreeItem(string);
    return {
      kind: MODULE_HIERARCHY_ROW_KIND.MODULE,
      seq: readModuleSeqFromTreeItem(item),
      displayId: readModuleDisplayNoFromTreeItem(item),
      arraySeq: readArraySeqFromTreeItem(array),
      stringSeq: stringSeq != null ? Number(stringSeq) : null,
      arrayDisplayId: readArrayDisplayNoFromTreeItem(array),
      stringDisplayId: readStringDisplayNoFromTreeItem(string)
    };
  }

  return null;
}

/** 맵 feature → hierarchy target (트리 동기화 전 맵 클릭용) */
export function panelMapFeatureToHierarchyTarget(feature) {
  if (!feature?.get) return null;

  const ft = (feature.get('featureType') || '').toLowerCase();
  if (ft === 'module') {
    return {
      kind: MODULE_HIERARCHY_ROW_KIND.MODULE,
      seq: readModuleSeq(feature) != null ? Number(readModuleSeq(feature)) : null,
      displayId: readModuleDisplayNo(feature),
      arraySeq: readArraySeq(feature) != null ? Number(readArraySeq(feature)) : null,
      stringSeq: readStringSeq(feature) != null ? Number(readStringSeq(feature)) : null,
      arrayDisplayId: readArrayDisplayNo(feature),
      stringDisplayId: readStringDisplayNo(feature)
    };
  }

  if (ft === 'string') {
    return {
      kind: MODULE_HIERARCHY_ROW_KIND.STRING,
      seq: readStringSeq(feature) != null ? Number(readStringSeq(feature)) : null,
      displayId: readStringDisplayNo(feature),
      stringDisplayId: readStringDisplayNo(feature),
      arraySeq: readArraySeq(feature) != null ? Number(readArraySeq(feature)) : null,
      arrayDisplayId: readArrayDisplayNo(feature)
    };
  }

  return null;
}

function isModuleFeature(feature) {
  const ft = (feature?.get?.('featureType') || '').toLowerCase();
  const name = feature?.get?.('name') || '';
  return ft === 'module' || name.startsWith('Module');
}

/**
 * 책갈피 앵커용 feature 목록 (모듈 geometry 우선)
 * @param {object} item — 트리 노드
 * @param {{ featureIndex?: object, moduleFeatures?: import('ol').Feature[], groupFeatures?: import('ol').Feature[] }} ctx
 */
export function resolvePanelBookmarkAnchorFeatures(item, ctx = {}) {
  const { featureIndex, moduleFeatures, groupFeatures } = ctx;

  if (item?.type === 'module' && moduleFeatures?.length) {
    return moduleFeatures.filter(isModuleFeature);
  }

  if (item?.type === 'string') {
    const stringSeq = readStringSeqFromTreeItem(item);
    if (stringSeq != null && featureIndex) {
      const related = getPanelMapFeaturesForString(featureIndex, String(stringSeq));
      const modules = related.filter(isModuleFeature);
      if (modules.length) return modules;
      if (related.length) return related;
    }
    if (groupFeatures?.length) {
      const modules = groupFeatures.filter(isModuleFeature);
      return modules.length ? modules : groupFeatures;
    }
  }

  if (item?.type === 'array') {
    const arraySeq = readArraySeqFromTreeItem(item);
    if (arraySeq != null && featureIndex) {
      const related = getPanelMapFeaturesForArray(featureIndex, String(arraySeq));
      const modules = related.filter(isModuleFeature);
      if (modules.length) return modules;
      if (related.length) return related;
    }
    if (groupFeatures?.length) {
      const modules = groupFeatures.filter(isModuleFeature);
      return modules.length ? modules : groupFeatures;
    }
  }

  return (moduleFeatures?.length ? moduleFeatures : groupFeatures) || [];
}

function findModuleNodeInTreeData(treeData, moduleSeq) {
  const target = String(moduleSeq);
  const walk = nodes => {
    for (const node of nodes || []) {
      if (
        node?.type === 'module' &&
        String(node.solarModuleSeq ?? node.seq ?? '') === target
      ) {
        return node;
      }
      const inChild = walk(node.children);
      if (inChild) {
        return inChild;
      }
    }
    return null;
  };
  return walk(treeData);
}

function findStringNodeInTreeData(treeData, stringSeq) {
  const seqStr = String(stringSeq);
  for (const arrayNode of treeData || []) {
    for (const stringNode of arrayNode.children || []) {
      if (readStringSeqFromTreeItem(stringNode) === seqStr) {
        return stringNode;
      }
    }
  }
  return null;
}

/**
 * 맵 Module feature → 트리 노드 (lazy index·feature fallback 포함)
 */
export function resolveModuleTreeItemForMapFeature(feature, treeData) {
  const moduleSeq = readModuleSeq(feature);
  if (moduleSeq == null || moduleSeq === '') {
    return null;
  }

  const fromTree = findModuleNodeInTreeData(treeData, moduleSeq);
  if (fromTree) {
    return fromTree;
  }

  const stringSeq = readStringSeq(feature);
  if (stringSeq != null) {
    const stringNode = findStringNodeInTreeData(treeData, stringSeq);
    if (stringNode?.id) {
      const lazyItem = getPanelTreeModuleChildren(stringNode.id).find(
        node => String(node.solarModuleSeq ?? node.seq ?? '') === String(moduleSeq)
      );
      if (lazyItem) {
        return lazyItem;
      }
    }
  }

  const displayNo = readModuleDisplayNo(feature);
  return {
    type: 'module',
    id: `module_${moduleSeq}`,
    seq: Number(moduleSeq),
    solarModuleSeq: Number(moduleSeq),
    displayNo,
    moduleId: displayNo,
    solarModuleId: feature.get?.('solarModuleId') ?? displayNo,
    name: feature.get?.('name') || `Module ${displayNo ?? moduleSeq}`
  };
}

function mergeHierarchyTargetFromFeature(target, feature) {
  if (!target || !feature) {
    return target;
  }
  const fromFeature = panelMapFeatureToHierarchyTarget(feature);
  if (!fromFeature) {
    return target;
  }
  return {
    ...target,
    seq: target.seq ?? fromFeature.seq,
    displayId: target.displayId ?? fromFeature.displayId,
    arraySeq: target.arraySeq ?? fromFeature.arraySeq,
    stringSeq: target.stringSeq ?? fromFeature.stringSeq,
    arrayDisplayId: target.arrayDisplayId ?? fromFeature.arrayDisplayId,
    stringDisplayId: target.stringDisplayId ?? fromFeature.stringDisplayId
  };
}

/** arraySeq/stringSeq만 있는 target에 트리에서 표시 번호 보강 */
export function enrichHierarchyTargetFromTreeData(target, treeData) {
  if (!target || !treeData?.length) {
    return target;
  }

  let arrayDisplayId = target.arrayDisplayId;
  let stringDisplayId = target.stringDisplayId;

  if (!arrayDisplayId && target.arraySeq != null) {
    const arrayNode = findArrayTreeItemBySeq(treeData, target.arraySeq);
    arrayDisplayId = readArrayDisplayNoFromTreeItem(arrayNode) ?? arrayDisplayId;
  }

  if (!stringDisplayId && target.stringSeq != null) {
    const stringNode = findStringNodeInTreeData(treeData, target.stringSeq);
    stringDisplayId = readStringDisplayNoFromTreeItem(stringNode) ?? stringDisplayId;
  }

  if (
    arrayDisplayId === target.arrayDisplayId &&
    stringDisplayId === target.stringDisplayId
  ) {
    return target;
  }

  return {
    ...target,
    arrayDisplayId,
    stringDisplayId
  };
}

/**
 * 책갈피 OL Overlay 생성 (없으면 null)
 * @returns {{ overlay: import('ol/Overlay').default, target: object }|null}
 */
export function createPanelHierarchyBookmarkOverlay(item, ctx = {}) {
  const features = resolvePanelBookmarkAnchorFeatures(item, ctx);
  const anchorFeature = features.find(isModuleFeature) ?? features[0] ?? null;

  let target = panelTreeItemToHierarchyTarget(item, ctx.treeData);
  if (target && anchorFeature) {
    target = mergeHierarchyTargetFromFeature(target, anchorFeature);
  } else if (!target && anchorFeature) {
    target = panelMapFeatureToHierarchyTarget(anchorFeature);
  }
  if (target) {
    target = enrichHierarchyTargetFromTreeData(target, ctx.treeData);
  }
  if (!target || !shouldShowMapSelectionOverlay(target)) {
    return null;
  }

  const position = resolveBookmarkAnchorPosition(features);
  if (!position) {
    return null;
  }

  const element = createMapHierarchyBookmarkOverlayElement(target, anchorFeature);

  return { target, position, element, anchorFeature, features };
}

export {
  shouldShowMapSelectionOverlay,
  resolveBookmarkAnchorPosition,
  createMapHierarchyBookmarkOverlayElement
};
